
import { useEffect, useRef } from 'react';
import './ModalView.scss';

interface ModalProps {
    children: React.ReactNode | React.ReactNode[];
    close: () => void;
    open: boolean;
    labelledById?: string;
};

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Component to display content in a modal dialogue
 *
 * @param children - Content to be displayed in dialogue
 * @param close - Callback to close the dialogue
 * @param open - State to control visibility of the dialogue
 * @param labelledById - ID to identify dialog
 * @constructor
 */
const ModalView: React.FC<ModalProps> = ({ children, close, open, labelledById }) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const okButtonRef = useRef<HTMLButtonElement | null>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!open){
            return;
        }
        // Remember to restore after close
        previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
        okButtonRef.current?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (!modalRef.current) {
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                close();
                return;
            }
            if (e.key !== 'Tab') {
                return;
            }

            // Fokussierbare Elemente des Dialogs sammeln und prüfen, ob der Fokus
            // vom ersten Element zurück bzw. vom letzten Element vorwärts springen soll.
            // In diesen Fällen den Fokus aufs letzte bzw. erste Element setzen, damit
            // der Fokus nicht aus dem Dialog herausspringt.
            const focusables = Array.from(
                modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
            ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
            if (focusables.length === 0) {
                e.preventDefault();
                return;
            }

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement as HTMLElement | null;

            if (e.shiftKey) {
                if (!active || active === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (!active || active === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            previouslyFocusedRef.current?.focus?.();
        };

    }, [open, close]);

    if (!open) {
        return null;
    }

    return (
        <div
            className="modal-view fixed-top"
            role="presentation"
        >
            <div
                className="modal-view--card"
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                areia-labelledBy={labelledById}
            >
                <div className="modal-view--content">
                    {children}
                </div>
                <div className="modal-view--actions">
                    <button
                        ref={okButtonRef}
                        type="button"
                        className="btn btn-primary"
                        onClick={close}
                        aria-label="Informationsfenster schließen"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ModalView;
